/*
 * Copyright (c) 2024. Bolmate B.V.
 */
import './style/platformChurn.scss';

import {ChangeFunction, CloseButton, Loader, SelectItemShape, WithBackground} from '@dvrd/dvr-controls';

import IDate from '@dvrd/idate';

import React, {MouseEventHandler, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {Churn, DataSplitMethod} from '../../../util/interfaces';

import {BolmateButton, BolmateSelect, DvrdDatePicker} from '../../controls';

interface Props {
    onClose: MouseEventHandler;
    onSubmit: MouseEventHandler;
    onChangeFromDate: (value: IDate | null) => void;
    onChangeToDate: (value: IDate | null) => void;
    onChangeSplitMethod: ChangeFunction<DataSplitMethod>;
    data: Array<Churn>;
    fromDate: IDate | null;
    toDate: IDate | null;
    splitMethod: DataSplitMethod;
    loading: boolean;
    active: boolean;
}

export default function PlatformChurn(props: Props) {
    const {t} = useTranslation();
    const splitMethodItems: Array<SelectItemShape> = useMemo(
        () => [
            {value: DataSplitMethod.HOUR, label: t('admin.churn.split.hour', 'Per uur')},
            {value: DataSplitMethod.DAY, label: t('admin.churn.split.day', 'Per dag')},
            {value: DataSplitMethod.WEEK, label: t('admin.churn.split.week', 'Per week')},
            {value: DataSplitMethod.MONTH, label: t('admin.churn.split.month', 'Per maand')},
            {value: DataSplitMethod.QUARTER, label: t('admin.churn.split.quarter', 'Per kwartaal')},
            {value: DataSplitMethod.YEAR, label: t('admin.churn.split.year', 'Per jaar')}
        ],
        [t]
    );
    const {
        data,
        loading,
        active,
        splitMethod,
        onChangeSplitMethod,
        onClose,
        onChangeToDate,
        onChangeFromDate,
        toDate,
        fromDate,
        onSubmit
    } = props;

    function onClearDate(field: 'from' | 'to') {
        return function () {
            if (field === 'from') onChangeFromDate(null);
            else onChangeToDate(null);
        };
    }

    function renderBody() {
        return (
            <div className='body-container'>
                {renderFilters()}
                {renderChurnContainer()}
            </div>
        );
    }

    function renderFilters() {
        return (
            <div className='filter-bar'>
                <BolmateSelect
                    onChange={onChangeSplitMethod}
                    value={splitMethod}
                    items={splitMethodItems}
                    label={t('admin.churn.display', 'Weergave')}
                />
                <div className='date-field'>
                    <DvrdDatePicker
                        onChange={onChangeFromDate}
                        value={fromDate}
                        label={t('admin.churn.fromDate', 'Vanaf datum')}
                        placeholder={t('admin.churn.datePlaceholder', 'dd-mm-jjjj')}
                        dateFormat='DD-MM-YYYY'
                        closeOnChange
                    />
                    <CloseButton onClick={onClearDate('from')} className='clear-btn' />
                </div>
                <div className='date-field'>
                    <DvrdDatePicker
                        onChange={onChangeToDate}
                        value={toDate}
                        label={t('admin.churn.toDate', 'T/m datum')}
                        placeholder={t('admin.churn.datePlaceholder', 'dd-mm-jjjj')}
                        dateFormat='DD-MM-YYYY'
                        closeOnChange
                    />
                    <CloseButton onClick={onClearDate('to')} className='clear-btn' />
                </div>
                <BolmateButton onClick={onSubmit} label={t('admin.churn.calculate', 'Berekenen')} disabled={loading} />
            </div>
        );
    }

    function renderChurnContainer() {
        return (
            <div className='churn-container'>
                <Loader active={loading} />
                <div className='row default-table-row head'>
                    <label>{t('admin.churn.col.period', 'PERIODE')}</label>
                    <label>{t('admin.churn.col.registered', 'GEREGISTREERD')}</label>
                    <label>{t('admin.churn.col.trialing', 'PROEFPERIODE')}</label>
                    <label>{t('admin.churn.col.free', 'GRATIS')}</label>
                    <label>{t('admin.churn.col.active', 'ACTIEF')}</label>
                    <label>{t('admin.churn.col.canceled', 'OPGEZEGD')}</label>
                    <label>{t('admin.churn.col.expired', 'VERLOPEN')}</label>
                    <label>{t('admin.churn.col.inactive', 'INACTIEF')}</label>
                </div>
                {data.map(renderChurn)}
            </div>
        );
    }

    function renderChurn(churn: Churn, index: number) {
        return (
            <div key={index} className='row default-table-row'>
                <label>
                    {churn.period}
                    {splitMethod === DataSplitMethod.HOUR ? ':00' : ''}
                </label>
                <label>{churn.registered}</label>
                {renderChurnCount(churn.trialing, churn.percentage_trialing)}
                {renderChurnCount(churn.free, churn.percentage_free)}
                {renderChurnCount(churn.active, churn.percentage_active)}
                {renderChurnCount(churn.canceled, churn.percentage_canceled)}
                {renderChurnCount(churn.expired, churn.percentage_expired)}
                {renderChurnCount(churn.inactive, churn.percentage_inactive)}
            </div>
        );
    }

    function renderChurnCount(count: number, percentage: number) {
        return (
            <label>
                {count} <span className='percentage'>({percentage.toString().replace('.', ',')}%)</span>
            </label>
        );
    }

    return (
        <WithBackground active={active} onClose={onClose}>
            <div className='platform-churn default-popup'>
                <div className='header-container'>
                    <label className='header-title'>{t('admin.churn.title', 'Abonnementen verloop')}</label>
                    <CloseButton onClick={onClose} />
                </div>
                {renderBody()}
                <div className='footer-container btn'>
                    <BolmateButton onClick={onClose} label={t('admin.churn.close', 'Sluiten')} disabled={loading} />
                </div>
            </div>
        </WithBackground>
    );
}
